import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'react-datocms/image';
import { Button, StepItem } from '@carbonify/ui';
import clsx from 'clsx';
import CtaButton from 'components/CtaButton';
import InlineRichText from 'components/RichContent/InlineRichText';
import { ResponsiveImage } from 'services/graphql';

import styles from './StepperModule.module.css';

interface StepBlock {
  id: string;
  title: string;
  content?: string;
  cta?: string;
  image: ResponsiveImage;
}

interface StepperModuleRecord {
  id: string;
  anchor?: string;
  __typename: string;
  title: string;
  subtitle: string;
  steps: StepBlock[];
  variant: 'animated' | 'manual';
  nextButtonLabel?: string;
}

const buttonProps = {
  variant: 'ghost' as const,
  size: 'xl' as const,
};

export default function StepperModule({
  title,
  subtitle,
  steps,
  variant,
  nextButtonLabel,
  anchor,
}: StepperModuleRecord) {
  const stepsRef = useRef<HTMLDivElement>(null);
  const isAnimated = variant === 'animated';
  const [currentStep, setCurrentStep] = useState(0);

  const onNextButton = () => {
    setCurrentStep((i) => i + 1);
  };

  const stepElements = useMemo(
    () =>
      steps.map(({ id, title, content, cta, image }, index) => (
        <StepItem
          key={id}
          title={<InlineRichText>{title}</InlineRichText>}
          content={content && <InlineRichText>{content}</InlineRichText>}
          stepNumber={index + 1}
          className={styles.step}
          aria-expanded={currentStep >= index ? true : undefined}
          // eslint-disable-next-line jsx-a11y/alt-text
          image={<Image data={image.responsiveImage} />}
          footer={
            isAnimated || index === steps.length - 1 ? (
              cta && (
                <CtaButton
                  {...buttonProps}
                  value={cta}
                />
              )
            ) : (
              <Button
                {...buttonProps}
                onClick={onNextButton}
              >
                {nextButtonLabel}
              </Button>
            )
          }
        />
      )),
    [steps, isAnimated, nextButtonLabel, currentStep],
  );

  useLayoutEffect(() => {
    const element = stepsRef.current;
    if (!element) return;

    const elementSneakPeekHeight = 16;
    let isVisible = false;
    let allItemsHeight = 0;
    let offset = 0;
    let cancelled = false;

    const getChildren = () => [...element.children] as HTMLDivElement[];
    const getStepItems = () => getChildren().filter((child) => child.classList.contains(styles.step));

    const equalizeStepHeights = () => {
      const stepItems = getStepItems();
      stepItems.forEach((item) => {
        item.style.minHeight = '';
      });

      const maxHeight = Math.ceil(Math.max(...stepItems.map((item) => item.getBoundingClientRect().height), 0));

      stepItems.forEach((item) => {
        item.style.minHeight = `${maxHeight}px`;
      });

      return maxHeight;
    };

    const applyStickyOffsets = (elementHeight: number) => {
      const elementChildren = getChildren();
      allItemsHeight = (elementChildren.length - 1) * elementSneakPeekHeight + elementHeight;
      offset = isAnimated ? Math.max((window.innerHeight - allItemsHeight) / 5, 64) : 0;

      elementChildren.forEach((child, index) =>
        child.style.setProperty(
          '--top',
          isAnimated ? `${offset + index * elementSneakPeekHeight}px` : `${index * elementSneakPeekHeight}px`,
        ),
      );

      if (!isAnimated) {
        element.style.setProperty('height', `${allItemsHeight}px`);
      }
    };

    const updateOffset = () => {
      const { top, height } = element.getBoundingClientRect();
      const absoluteTop = top < 0 ? -top : 0;
      const topScrollHeight = absoluteTop + allItemsHeight + elementSneakPeekHeight + offset;
      const topOffset = Math.max(topScrollHeight - height, 0);
      if (topOffset) {
        element.style.setProperty('--r-top', `${topOffset}px`);
      }
    };

    let ignoreResizeEcho = false;
    let layoutFrame: number | undefined;
    let resizeEchoFrame: number | undefined;
    let lastViewportWidth = window.innerWidth;
    let lastViewportHeight = window.innerHeight;

    const applyLayout = () => {
      if (cancelled) return;

      ignoreResizeEcho = true;
      if (resizeEchoFrame !== undefined) cancelAnimationFrame(resizeEchoFrame);
      applyStickyOffsets(equalizeStepHeights());
      if (isAnimated) {
        updateOffset();
      }
      lastViewportWidth = window.innerWidth;
      lastViewportHeight = window.innerHeight;

      resizeEchoFrame = requestAnimationFrame(() => {
        resizeEchoFrame = undefined;
        ignoreResizeEcho = false;
      });
    };

    const scheduleLayout = () => {
      if (cancelled || layoutFrame !== undefined) return;

      // Resizing observed cards during delivery can trigger a ResizeObserver loop error.
      layoutFrame = requestAnimationFrame(() => {
        layoutFrame = undefined;
        applyLayout();
      });
    };

    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        if (cancelled || ignoreResizeEcho) return;

        const viewportChanged = window.innerWidth !== lastViewportWidth || window.innerHeight !== lastViewportHeight;
        const onlyDocumentResized = entries.every((entry) => entry.target === document.documentElement);

        // Equalizing card heights can grow the page without the viewport changing.
        if (onlyDocumentResized && !viewportChanged) return;

        scheduleLayout();
      });

      getStepItems().forEach((item) => observer.observe(item));
      observer.observe(document.documentElement);
      resizeObserver = observer;
    } else {
      window.addEventListener('resize', scheduleLayout);
    }

    applyLayout();

    let intersectionObserver: IntersectionObserver | undefined;
    if (isAnimated) {
      const handleVisibility: IntersectionObserverCallback = (entries) => {
        const isIntersecting = entries[0].isIntersecting;
        if (isIntersecting && !isVisible) {
          updateOffset();
          window.addEventListener('scroll', updateOffset);
          element.classList.add(styles.rendered);
        }
        if (!isIntersecting && isVisible) {
          updateOffset();
          window.removeEventListener('scroll', updateOffset);
          element.classList.remove(styles.rendered);
        }
        isVisible = isIntersecting;
      };

      intersectionObserver = new IntersectionObserver(handleVisibility, {});
      intersectionObserver.observe(element);
    }

    return () => {
      cancelled = true;
      if (layoutFrame !== undefined) cancelAnimationFrame(layoutFrame);
      if (resizeEchoFrame !== undefined) cancelAnimationFrame(resizeEchoFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleLayout);
      intersectionObserver?.unobserve(element);
      window.removeEventListener('scroll', updateOffset);
      getStepItems().forEach((item) => {
        item.style.minHeight = '';
      });
    };
  }, [isAnimated, steps]);

  return (
    <section
      data-testid={`stepper`}
      id={anchor}
      className={clsx(styles.module)}
    >
      {title && (
        <InlineRichText
          as="h2"
          className={styles.title}
        >
          {title}
        </InlineRichText>
      )}
      {subtitle && (
        <InlineRichText
          as="p"
          className={styles.subtitle}
        >
          {subtitle}
        </InlineRichText>
      )}
      <div
        className={clsx(styles.steps, styles[variant])}
        ref={stepsRef}
      >
        {stepElements}
        <div />
      </div>
    </section>
  );
}
